import React from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import numeral from 'numeral';
import moment from 'moment';
import { useWatch } from 'react-hook-form';
import { OrderActions } from './order-actions';
import { ConfirmTaskButton } from '@src/components/common/confirm-task-button/confirm-task-button';
import { formatDateTime } from '@src/utils/date/formatDateTime';
import { cn } from '@src/utils/ui';

export const OrderTable = props => {
    const { buyerView } = props;
    const items = useWatch({ name: 'items' });

    return (
        <div className='tw-d-none lg:tw-block'>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontSize: '16px' }} width={100}>
                            Task Number
                        </TableCell>
                        <TableCell sx={{ fontSize: '16px' }} width={100}>
                            Issue Date
                        </TableCell>
                        <TableCell sx={{ fontSize: '16px' }} width={100}>
                            {buyerView ? 'Tasker' : 'Customer'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '16px' }} width={100}>
                            Task Date
                        </TableCell>
                        <TableCell sx={{ fontSize: '16px' }} width={100}>
                            Total
                        </TableCell>
                        <TableCell sx={{ fontSize: '16px' }} width={100}>
                            Task Status
                        </TableCell>
                        <TableCell sx={{ fontSize: '16px' }} width={240}>
                            Fulfillment Status
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items?.map(order => (
                        <Row key={order.id} order={order} {...props} />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

const Row = props => {
    const { order, buyerView } = props;
    order.isFulfillEnabled = order.isFulfillEnabled && moment().isSameOrAfter(moment.unix(order.taskDate));

    return (
        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell sx={{ fontSize: '16px' }} component='th' scope='row'>
                <a
                    href={`/${buyerView ? 'purchase' : 'merchants/order'}/detail/${order.invoiceNo}`}
                    className='tw-underline tw-text-[#0050C4] hover:tw-underline hover:tw-text-[#0050C4]'
                >
                    {order.orderNumber}
                </a>
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }}>{formatDateTime(order.orderIssueDate, 'MM/DD/YYYY')}</TableCell>
            <TableCell sx={{ fontSize: '16px' }}>{buyerView ? order.providerName : order.customerName}</TableCell>
            <TableCell sx={{ fontSize: '16px' }}>{formatDateTime(order.taskDate, 'MM/DD/YYYY')}</TableCell>
            <TableCell sx={{ fontSize: '16px' }} className='tw-font-medium'>
                {numeral(order.total).format('$0,0.00')}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }}>{order.orderStatus}</TableCell>
            <TableCell sx={{ fontSize: '16px' }}>
                <div
                    className={cn([
                        'tw-flex tw-gap-[8px]',
                        props.buyerView
                            ? ' tw-items-center tw-flex-row-reverse tw-max-w-[300px]'
                            : 'tw-items-start tw-flex-col-reverse'
                    ])}
                >
                    <OrderActions
                        orderId={order.id}
                        isEnabled={order.isFulfillEnabled}
                        fulfillOrder={props.fulfillOrder}
                        isCreatedInBookNow={order.isCreatedInBookNow}
                        fulfillmentStatus={order.fulfillmentStatus}
                        onCancelAndRefund={props.cancelAndRefund}
                        buyerView={props.buyerView}
                        taskDate={order.taskDate}
                        total={order.total}
                        channelId={order.channelId}
                        itemId={order.itemId}
                        cartId={order.cartId}
                        buyerHasSubmittedReview={order.buyerHasSubmittedReview}
                        submitFeedbackForCartItem={props.submitFeedbackForCartItem}
                    />
                    {!props.buyerView ? (
                        <ConfirmTaskButton
                            channelId={order.channelId}
                            orderId={order.id}
                            confirmOrder={props.confirmOrder}
                            isCreatedInBookNow={order.isCreatedInBookNow}
                        />
                    ) : null}
                    {order.isCreatedInBookNow && !props.buyerView ? (
                        <div className='tw-text-[#0050C4] tw-font-[500] tw-w-full'>Via Book Now</div>
                    ) : null}
                </div>
            </TableCell>
        </TableRow>
    );
};

OrderTable.propTypes = {
    buyerView: PropTypes.bool,
    fulfillOrder: PropTypes.func,
    cancelAndRefund: PropTypes.func.isRequired
};

Row.propTypes = {
    ...OrderTable.propTypes,
    order: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        invoiceNo: PropTypes.string.isRequired,
        orderNumber: PropTypes.string.isRequired,
        orderIssueDate: PropTypes.number.isRequired,
        customerName: PropTypes.string.isRequired,
        taskDate: PropTypes.number.isRequired,
        total: PropTypes.number.isRequired,
        orderStatus: PropTypes.string,
        fulfillmentStatus: PropTypes.string,
        channelId: PropTypes.string,
        itemId: PropTypes.string,
        cartId: PropTypes.string,
        buyerHasSubmittedReview: PropTypes.bool
    }).isRequired
};
